import { daysBetween, type CalendarDate } from "../shared/calendar-date";
import type { GoalPhase } from "../goals/goal";
import { maintenanceRange, signedWeeklyRate } from "../goals/goal";
import { requireFiniteNonNegative, requireFinitePositive } from "../shared/validation";

export interface CheckInQuestionnaire {
  readonly loggedEverything: boolean;
  /** Self-reported adherence to allocated calories, from 0 to 100. */
  readonly calorieCompliancePercent: number;
  readonly notes?: string;
}

export interface CalorieRecommendationPolicy {
  readonly version: string;
  readonly energyPerKilogramKcal: number;
  readonly minimumCompliancePercent: number;
  readonly minimumAdjustmentCalories: number;
  readonly maximumDailyIncreaseCalories: number;
  readonly maximumDailyDecreaseCalories: number;
  readonly minimumRecommendedCalories: number;
  readonly maximumRecommendedCalories: number;
}

/**
 * Conservative, versioned feedback limits. User review remains mandatory at
 * every check-in. Evidence boundary and source review are recorded in
 * docs/architecture-decisions/0001-calorie-safety-policy-v1.md.
 */
export const DEFAULT_CALORIE_RECOMMENDATION_POLICY_V1: CalorieRecommendationPolicy = Object.freeze({
  version: "calorie-recommendation-v1",
  energyPerKilogramKcal: 7_700,
  minimumCompliancePercent: 80,
  minimumAdjustmentCalories: 50,
  maximumDailyIncreaseCalories: 250,
  maximumDailyDecreaseCalories: 250,
  minimumRecommendedCalories: 1_200,
  maximumRecommendedCalories: 5_000,
});

export type RecommendationReason =
  | "goal_correction"
  | "within_maintenance_tolerance"
  | "insufficient_weight_data"
  | "incomplete_logging"
  | "low_calorie_compliance"
  | "daily_floor";

export interface CalorieRecommendation {
  readonly policyVersion: string;
  readonly previousBaselineCalories: number;
  readonly recommendedBaselineCalories: number;
  readonly dailyAdjustmentCalories: number;
  readonly uncappedDailyAdjustmentCalories: number;
  readonly reason: RecommendationReason;
  readonly previousTrendWeightKilograms?: number;
  readonly currentTrendWeightKilograms?: number;
}

export interface RecommendationInput {
  readonly previousBaselineCalories: number;
  readonly goal: GoalPhase;
  readonly questionnaire: CheckInQuestionnaire;
  readonly previousTrend?: { readonly date: CalendarDate; readonly kilograms: number };
  readonly currentTrend?: { readonly date: CalendarDate; readonly kilograms: number };
  readonly policy: CalorieRecommendationPolicy;
}

export function recommendDailyCalories(input: RecommendationInput): CalorieRecommendation {
  validateCalorieRecommendationPolicy(input.policy);
  if (!Number.isInteger(input.previousBaselineCalories) || input.previousBaselineCalories <= 0) {
    throw new Error("Previous baseline calories must be a positive whole number");
  }
  if (!Number.isFinite(input.questionnaire.calorieCompliancePercent) || input.questionnaire.calorieCompliancePercent < 0 || input.questionnaire.calorieCompliancePercent > 100) {
    throw new Error("Calorie compliance must be between 0 and 100");
  }

  const hold = (reason: RecommendationReason): CalorieRecommendation => ({
    policyVersion: input.policy.version,
    previousBaselineCalories: input.previousBaselineCalories,
    recommendedBaselineCalories: input.previousBaselineCalories,
    dailyAdjustmentCalories: 0,
    uncappedDailyAdjustmentCalories: 0,
    reason,
    previousTrendWeightKilograms: input.previousTrend?.kilograms,
    currentTrendWeightKilograms: input.currentTrend?.kilograms,
  });

  if (!input.questionnaire.loggedEverything) return hold("incomplete_logging");
  if (input.questionnaire.calorieCompliancePercent < input.policy.minimumCompliancePercent) return hold("low_calorie_compliance");
  if (!input.previousTrend || !input.currentTrend) return hold("insufficient_weight_data");
  requireFinitePositive(input.previousTrend.kilograms, "Previous trend weight");
  requireFinitePositive(input.currentTrend.kilograms, "Current trend weight");
  const elapsedDays = daysBetween(input.previousTrend.date, input.currentTrend.date);
  if (elapsedDays <= 0) return hold("insufficient_weight_data");

  let weightCorrectionKg: number;
  if (input.goal.mode === "maintain") {
    const range = maintenanceRange(input.goal);
    if (input.currentTrend.kilograms >= range.minimumKilograms && input.currentTrend.kilograms <= range.maximumKilograms) {
      return hold("within_maintenance_tolerance");
    }
    const closestBoundary = input.currentTrend.kilograms < range.minimumKilograms ? range.minimumKilograms : range.maximumKilograms;
    weightCorrectionKg = closestBoundary - input.currentTrend.kilograms;
  } else {
    const desiredChangeKg =
      input.previousTrend.kilograms * (signedWeeklyRate(input.goal) / 100) * (elapsedDays / 7);
    const actualChangeKg = input.currentTrend.kilograms - input.previousTrend.kilograms;
    weightCorrectionKg = desiredChangeKg - actualChangeKg;
  }

  const uncapped = (weightCorrectionKg * input.policy.energyPerKilogramKcal) / elapsedDays;
  if (Math.abs(uncapped) < Number.EPSILON) return hold("goal_correction");
  let adjustment = Math.sign(uncapped) * Math.max(Math.abs(uncapped), input.policy.minimumAdjustmentCalories);
  adjustment = Math.max(-input.policy.maximumDailyDecreaseCalories, Math.min(input.policy.maximumDailyIncreaseCalories, adjustment));
  const globallyBounded = Math.max(
    input.policy.minimumRecommendedCalories,
    Math.min(input.policy.maximumRecommendedCalories, input.previousBaselineCalories + adjustment),
  );
  // Global recommendation bounds must not turn into a sudden weekly jump when
  // a user-reviewed baseline already sits outside them.
  const changeBounded = Math.max(
    input.previousBaselineCalories - input.policy.maximumDailyDecreaseCalories,
    Math.min(input.previousBaselineCalories + input.policy.maximumDailyIncreaseCalories, globallyBounded),
  );
  const recommended = Math.round(changeBounded);
  return {
    policyVersion: input.policy.version,
    previousBaselineCalories: input.previousBaselineCalories,
    recommendedBaselineCalories: recommended,
    dailyAdjustmentCalories: recommended - input.previousBaselineCalories,
    uncappedDailyAdjustmentCalories: uncapped,
    reason: "goal_correction",
    previousTrendWeightKilograms: input.previousTrend.kilograms,
    currentTrendWeightKilograms: input.currentTrend.kilograms,
  };
}

export function validateCalorieRecommendationPolicy(policy: CalorieRecommendationPolicy): CalorieRecommendationPolicy {
  if (!policy.version.trim()) throw new Error("Recommendation policy version is required");
  requireFinitePositive(policy.energyPerKilogramKcal, "Energy per kilogram");
  requireFiniteNonNegative(policy.minimumCompliancePercent, "Minimum compliance");
  if (policy.minimumCompliancePercent > 100) throw new Error("Minimum compliance cannot exceed 100");
  requireFiniteNonNegative(policy.minimumAdjustmentCalories, "Minimum adjustment");
  requireFiniteNonNegative(policy.maximumDailyIncreaseCalories, "Maximum increase");
  requireFiniteNonNegative(policy.maximumDailyDecreaseCalories, "Maximum decrease");
  requireFinitePositive(policy.minimumRecommendedCalories, "Minimum recommended calories");
  requireFinitePositive(policy.maximumRecommendedCalories, "Maximum recommended calories");
  if (policy.minimumRecommendedCalories > policy.maximumRecommendedCalories) throw new Error("Recommended calorie bounds are inverted");
  return policy;
}
